import { useMemo, useState } from 'react';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { djs } from '@duke-hq/djs';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Select } from 'chakra-react-select';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { scheduleService } from '@duke-hq/svc-api/student/services/schedulerService';
import { CourseSegment, isWorkSchedule, WorkSchedule } from '@duke-hq/shared-constants/enrolments';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useSemesterEndDate } from '@duke-hq/ui/components/hooks/useSemesterEndDate';
import { CourseSupersessionEventTypeSchema } from '@duke-hq/svc-api-db/schemas/enums/CourseSupersessionEventType.schema';

type UpdateEnrolmentModalProps = {
  isOpen: boolean;
  onClose: () => void;
  enrolment: NonNullable<RouterOutputs['enrolment']['byIdWithResourceStatus']>;
  supersededCourse?: RouterOutputs['courses']['courseSupersessionHistory'];
};

const UpdateEnrolmentSettingsSchema = z.object({
  start_at_date: z.string(),
  semester_2_start_at_date: z.string(),
  course_segment: z.enum(CourseSegment),
  work_schedule: z.enum(WorkSchedule),
});

type Course = {
  identifier: string | null;
  title: string;
};

type SupersededCourse = Course & {
  id: string;
};

function formatCourseDisplay(course: { identifier?: string | null; title: string } | null) {
  if (course == null) {
    return null;
  }

  const hasIdentifier = course.identifier != null && course.identifier !== '';

  if (hasIdentifier) {
    return `${course.identifier} ${course.title}`;
  }

  return course.title;
}

function getSupersessionMessage(course: Course, supersededByCourse: SupersededCourse | null) {
  const currentCourseDisplay = formatCourseDisplay(course);
  const supersededCourseDisplay = formatCourseDisplay(supersededByCourse);

  return {
    currentCourseDisplay,
    supersededCourseDisplay,
  };
}

function AddSemesterModal({
  enrolment,
  isOpen,
  onClose,
  supersededCourse,
}: UpdateEnrolmentModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();
  const [isConfirmingUpdate, setIsConfirmingUpdate] = useState(false);

  const updateCourseEnrolment = trpcAdmin.enrolment.updateCourseEnrolment.useMutation({
    async onSuccess() {
      toast({
        status: 'success',
        title: t`Enrolment has been updated`,
      });

      await utils.enrolment.byIdWithResourceStatus.refetch({
        enrolmentId: Number(enrolment.id),
      });

      onClose();
    },
    onError(error) {
      toast({
        status: 'error',
        title: t`An error occurred while updating the enrolment`,
        description: error?.message ?? t`An error occurred while updating the enrolment`,
      });
    },
    onSettled() {
      setIsConfirmingUpdate(false);
    },
  });

  const methods = useForm<{
    start_at_date: string;
    semester_2_start_at_date: string;
    course_segment: CourseSegment;
    work_schedule: WorkSchedule;
  }>({
    resolver: zodResolver(UpdateEnrolmentSettingsSchema),
    defaultValues: {
      start_at_date: djs(enrolment.start_at_date).format('YYYY-MM-DD'),
      semester_2_start_at_date: djs(scheduleService.getSemesterStartDate(2, enrolment)).format(
        'YYYY-MM-DD',
      ),
      course_segment: CourseSegment.FULL_COURSE,
      work_schedule: isWorkSchedule(enrolment.work_schedule)
        ? enrolment.work_schedule
        : WorkSchedule.FULL_TIME,
    },
  });

  const [startAt, workSchedule] = methods.watch(['start_at_date', 'work_schedule']);

  const handleConfirmUpdate = async () => {
    await methods.handleSubmit(
      (data) => {
        updateCourseEnrolment.mutate({
          enrolment_id: enrolment.id,
          start_at_date: djs.utc(enrolment.start_at_date).format('YYYY-MM-DD'),
          semester_2_start_at_date: djs.utc(data.semester_2_start_at_date).format('YYYY-MM-DD'),
          user_id: enrolment.user.id,
          enrolment_contract_type: enrolment.enrolment_contract_type,
          contract_category: enrolment.contract_category,
          course_segment: data.course_segment,
          work_schedule: data.work_schedule,
          isComplimentary: enrolment.isComplimentary,
        });
      },
      () => {
        setIsConfirmingUpdate(false);
      },
    )();
  };

  const {
    control,
    formState: { errors },
  } = methods;
  const semesterTwoMinStartDate = useSemesterEndDate({
    workSchedule,
    startAt,
  });

  const { currentCourseDisplay, supersededCourseDisplay } = useMemo(() => {
    if (
      enrolment.course != null &&
      supersededCourse != null &&
      supersededCourse.supersededByCourse != null &&
      supersededCourse.eventType === CourseSupersessionEventTypeSchema.enum.SUPERSEDED_TOGGLED_ON
    ) {
      return getSupersessionMessage(enrolment.course, supersededCourse.supersededByCourse);
    }
    return { currentCourseDisplay: null, supersededCourseDisplay: null };
  }, [enrolment.course, supersededCourse]);

  return (
    <>
      <Modal
        size="3xl"
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
      >
        <ModalOverlay />
        <ModalContent
          padding={4}
          h={'auto'}
          as={'form'}
          onSubmit={methods.handleSubmit(() => {
            setIsConfirmingUpdate(true);
          })}
        >
          <ModalCloseButton />
          <ModalBody pb={6} h="full">
            <FormProvider {...methods}>
              <VStack gap="8px" w="full" py="8px">
                <HStack w="full" justify="space-between" align="start" gap="10px">
                  <FormControl isInvalid={!!errors.semester_2_start_at_date} isRequired>
                    <FormLabel htmlFor="semester_2_start_at_date">
                      <Trans>Start date</Trans>
                    </FormLabel>
                    <Input
                      id="semester_2_start_at_date"
                      {...methods.register('semester_2_start_at_date', {
                        required: t`Start date is required.`,
                        validate: (value) => djs(value).isValid() || t`Please enter a valid date.`,
                      })}
                      type="date"
                      defaultValue={djs(semesterTwoMinStartDate).format('YYYY-MM-DD')}
                      min={djs(semesterTwoMinStartDate).format('YYYY-MM-DD')}
                    />
                    <FormErrorMessage>{errors.semester_2_start_at_date?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>
                <HStack w="full" justify="space-between" align="start" gap="10px">
                  <FormControl isInvalid={!!errors.course_segment} isRequired opacity={'40%'}>
                    <FormLabel htmlFor="course_segment">
                      <Trans>Course segment</Trans>
                    </FormLabel>
                    <Controller
                      name="course_segment"
                      control={control}
                      rules={{ required: t`Course segment is required.` }}
                      render={({ field }) => (
                        <Select
                          id="course_segment"
                          placeholder={t`Select option`}
                          value={{
                            label: '2nd Semester',
                            value: CourseSegment.FULL_COURSE,
                          }}
                          isDisabled
                          onChange={(val: { label: string; value: CourseSegment } | null) =>
                            field.onChange(val?.value ?? '')
                          }
                        />
                      )}
                    />
                    <FormErrorMessage>{errors.course_segment?.message}</FormErrorMessage>
                  </FormControl>
                </HStack>
              </VStack>
            </FormProvider>
          </ModalBody>
          <ModalFooter>
            {supersededCourse != null &&
            supersededCourse.supersededByCourse != null &&
            supersededCourse.eventType ===
              CourseSupersessionEventTypeSchema.enum.SUPERSEDED_TOGGLED_ON ? (
              <VStack w="full" gap="8px" alignItems="right" justifyContent="center">
                <Alert status="warning">
                  <AlertIcon alignSelf="flex-start" mt="2px" />
                  <AlertDescription>
                    <VStack align="start" spacing="0px" gap="0px">
                      <Text fontSize="sm" lineHeight="1.5">
                        {currentCourseDisplay !== null ? (
                          <>
                            <Text as="span" fontWeight="bold">
                              {currentCourseDisplay}
                            </Text>{' '}
                            <Trans>has been superseded</Trans>
                          </>
                        ) : (
                          <Trans>This course has been superseded</Trans>
                        )}
                      </Text>
                      <Text fontSize="sm" lineHeight="1.5">
                        {supersededCourseDisplay !== null ? (
                          <>
                            <Trans>Transition the student to</Trans>{' '}
                            <Text as="span" fontWeight="bold">
                              {supersededCourseDisplay}
                            </Text>
                          </>
                        ) : (
                          <Trans>Transition the student to the new course</Trans>
                        )}
                      </Text>
                    </VStack>
                  </AlertDescription>
                </Alert>
              </VStack>
            ) : (
              <VStack w="full" gap="8px" alignItems="right" justifyContent="center">
                <Alert status="warning">
                  <HStack>
                    <AlertIcon />
                  </HStack>
                  <AlertDescription>
                    <Text fontSize="sm">
                      <Trans>
                        This will rebuild the student enrolment schedule. This is final.
                      </Trans>
                    </Text>
                  </AlertDescription>
                </Alert>
                <Button type="submit">
                  <Trans>Rebuild schedule</Trans>
                </Button>
              </VStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
      <ConfirmationDialog
        isOpen={isConfirmingUpdate}
        onClose={() => {
          setIsConfirmingUpdate(false);
        }}
        title={<Trans>Update enrolment</Trans>}
        body={
          <Trans>
            Are you sure you want to update this enrolment and rebuild its schedule? This is final.
          </Trans>
        }
        onConfirm={handleConfirmUpdate}
        confirmLabel={<Trans>Yes, rebuild schedule</Trans>}
        variant="destructive"
        isLoading={updateCourseEnrolment.isPending}
      />
    </>
  );
}

export default AddSemesterModal;
